firebird.factory('mobileAVDeviceService', [
    '$window',
    '$q',
    '$rootScope',
    'logService',
    'helperService',
    function ($window, $q, $rootScope, logService, helperService) {
        const logger = logService.createLoggerWithSource('mobileAVDeviceService');

        var service = {
            setMobileAudioOutput: setMobileAudioOutput,
            setIOSAudioOutput: setIOSAudioOutput,
            setAndroidAudioOutput: setAndroidAudioOutput,
            checkCurrentMobileOutput: checkCurrentMobileOutput,
            initHeadsetDetection: initHeadsetDetection,
            checkBuiltInAudioCapabilities: checkBuiltInAudioCapabilities,
            checkForConnectedHeadset: checkForConnectedHeadset,

            //Currently only use these for Android
            hasBuiltInSpeaker: null,
            hasBuiltInEarpiece: null,

            //General ENUMS for other controllers/services to reference
            audioOutputs: {
                EARPIECE: 'EARPIECE',
                SPEAKER: 'SPEAKER'
            },

            //These ENUMS are for compatibility with each platform's respective audio-output plugins
            iOSAudioOutputs: {
                EARPIECE: 'default',
                SPEAKER: 'speaker'
            },
            androidAudioOutputs: {
                EARPIECE: $window.AudioToggle ? $window.AudioToggle.NORMAL : 'normal',
                SPEAKER: $window.AudioToggle ? $window.AudioToggle.SPEAKER : 'speaker'
            }
        };

        return service;

        ////////////////////////

        function setMobileAudioOutput(newAudioOutput) {
            var deferred = $q.defer();

            if (!service.audioOutputs.hasOwnProperty(newAudioOutput)) {
                deferred.reject('Invalid audio output: ' + newAudioOutput);
                return deferred.promise;
            }

            if (!helperService.isMobile()) {
                deferred.reject('Client is not on mobile device');
                return deferred.promise;
            }

            var devicePlatform = helperService.getDevicePlatform();
            var newAudioOutputEnum, audioOutputMethod;

            if (devicePlatform === 'iOS') {
                newAudioOutputEnum = service.iOSAudioOutputs[newAudioOutput];
                audioOutputMethod = 'setIOSAudioOutput';
            } else if (devicePlatform === 'Android') {
                newAudioOutputEnum = service.androidAudioOutputs[newAudioOutput];
                audioOutputMethod = 'setAndroidAudioOutput';
            } else {
                deferred.reject('Invalid Mobile Device Platform: ' + devicePlatform);
                return deferred.promise;
            }

            if (newAudioOutputEnum && audioOutputMethod) {
                logger.log('Setting Mobile Audio Output to ' + newAudioOutputEnum);
                service[audioOutputMethod](newAudioOutputEnum)
                    .then(function () {
                        logger.log('Successfully set Mobile Audio Output to ' + newAudioOutputEnum);
                        deferred.resolve();
                    })
                    .catch(function (e) {
                        logger.error('Error setting Mobile Audio Output to ' + newAudioOutputEnum, e);
                        deferred.reject(e);
                    });
            } else {
                deferred.reject(
                    'Invalid audio output params - ' +
                        'newAudioOutputEnum: ' +
                        newAudioOutputEnum +
                        ', audioOutputMethod:' +
                        audioOutputMethod
                );
            }

            return deferred.promise;
        }

        function setIOSAudioOutput(newAudioOutput) {
            var deferred = $q.defer();

            try {
                $window.cordova.plugins.audioroute.overrideOutput(
                    newAudioOutput,
                    function () {
                        deferred.resolve();
                    },
                    function (err) {
                        deferred.reject(err);
                    }
                );
            } catch (e) {
                deferred.reject(e);
            }

            return deferred.promise;
        }

        function setAndroidAudioOutput(newAudioOutput) {
            var deferred = $q.defer();

            try {
                //There is no success callback from AudioToggle plugin
                $window.AudioToggle.setAudioMode(newAudioOutput);
                deferred.resolve();
            } catch (e) {
                deferred.reject(e);
            }

            return deferred.promise;
        }

        function checkCurrentMobileOutput() {
            var deferred = $q.defer();
            var devicePlatform = helperService.getDevicePlatform();

            if (devicePlatform === 'iOS') {
                $window.cordova.plugins.audioroute.currentOutputs(
                    function (successArg) {
                        if (successArg && successArg.length > 0 && successArg[0] === 'builtin-speaker') {
                            deferred.resolve({ isMobileSpeakerOn: true });
                        } else {
                            deferred.resolve({ isMobileSpeakerOn: false });
                        }
                    },
                    function (err) {
                        deferred.reject(err);
                    }
                );
            } else if (devicePlatform === 'Android') {
                $window.AudioToggle.isSpeakerphoneOn(
                    function (successArg) {
                        if (successArg === true || successArg === 'true') {
                            deferred.resolve({ isMobileSpeakerOn: true });
                        } else {
                            deferred.resolve({ isMobileSpeakerOn: false });
                        }
                    },
                    function (err) {
                        deferred.reject(err);
                    }
                );
            } else {
                deferred.reject('Invalid Mobile Device Platform: ' + devicePlatform);
            }

            return deferred.promise;
        }

        function initHeadsetDetection() {
            if (!helperService.isMobile()) {
                return;
            }

            if ($window.HeadsetDetection && $window.HeadsetDetection.registerRemoteEvents) {
                $window.HeadsetDetection.registerRemoteEvents(function (status) {
                    switch (status) {
                        case 'headsetAdded':
                            logger.log('Mobile Headset was added');
                            $rootScope.$broadcast('mobile-headset-change-detected', { state: 'added' });
                            break;
                        case 'headsetRemoved':
                            logger.log('Mobile Headset was removed');
                            $rootScope.$broadcast('mobile-headset-change-detected', { state: 'removed' });
                            break;
                        default:
                            logger.log('Unknown status:', status);
                            break;
                    }
                });
            } else {
                logger.log('Cordova plugin HeadsetDetection not found');
            }

            //Modern Android devices require USB-C adapters for wired audio input.
            //These events aren't consistently detected by the HeadsetDetection plugin,
            //which is where this usbDetection plugin comes in handly
            if (
                $window.cordova &&
                $window.cordova.plugins &&
                $window.cordova.plugins.usbDetection &&
                $window.cordova.plugins.usbDetection.listen
            ) {
                $window.cordova.plugins.usbDetection.listen(function () {
                    //This callback doesn't indicate whether or not a USB connection was added or removed,
                    //but we only need to know that it changed
                    $rootScope.$broadcast('mobile-headset-change-detected');
                });
            }
        }

        function checkBuiltInAudioCapabilities() {
            //Currently only available for Android
            if (helperService.getDevicePlatform() !== 'Android') {
                return;
            }

            if ($window.HeadsetDetection) {
                if ($window.HeadsetDetection.hasBuiltInSpeaker) {
                    logger.log('Checking for built-in speaker');

                    $window.HeadsetDetection.hasBuiltInSpeaker(function (hasBuiltInSpeaker) {
                        service.hasBuiltInSpeaker = hasBuiltInSpeaker === 'true' || hasBuiltInSpeaker === true;
                        logger.log('Device has built-in speaker:', service.hasBuiltInSpeaker);
                    });
                } else {
                    logger.log('Unable to check for built-in speaker');
                }

                if ($window.HeadsetDetection.hasBuiltInEarpiece) {
                    logger.log('Checking for built-in earpiece');

                    $window.HeadsetDetection.hasBuiltInEarpiece(function (hasBuiltInEarpiece) {
                        service.hasBuiltInEarpiece = hasBuiltInEarpiece === 'true' || hasBuiltInEarpiece === true;
                        logger.log('Device has built-in earpiece:', service.hasBuiltInEarpiece);
                    });
                } else {
                    logger.log('Unable to check for built-in earpiece');
                }
            }
        }

        function checkForConnectedHeadset() {
            var deferred = $q.defer();

            try {
                $window.HeadsetDetection.detect(
                    function (hasConnectedHeadset) {
                        deferred.resolve(hasConnectedHeadset === 'true' || hasConnectedHeadset === true);
                    },
                    function (err) {
                        deferred.reject(err);
                    }
                );
            } catch (e) {
                deferred.reject(e);
            }

            return deferred.promise;
        }
    }
]);
